import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  FileSignature,
  FileText,
  Filter,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useToast } from '../components/Toast';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { Act, ActStatus, ContractStatus, deleteAct, getActs } from '../services';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { RootState } from '../store';
import { DateInput } from '../components/DateInput';

type Option = { id: string; name: string };
type SortKey = 'createdAt' | 'performedAt' | 'status' | 'plannedStartAt';

const LIMIT = 20;

const statusLabels: Record<ActStatus, string> = {
  PLANNED: 'Planifié',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const statusBadgeClasses: Record<ActStatus, string> = {
  PLANNED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

const contractStatusLabels: Record<ContractStatus, string> = {
  DRAFT: 'Brouillon',
  PENDING: 'En attente',
  SIGNED: 'Signé',
  EXPIRED: 'Expiré',
  CANCELLED: 'Annulé',
};

const contractStatusBadgeClasses: Record<ContractStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  PENDING: 'bg-yellow-100 text-yellow-800',
  SIGNED: 'bg-emerald-100 text-emerald-800',
  EXPIRED: 'bg-orange-100 text-orange-800',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function ActsListPage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';

  const [acts, setActs] = useState<Act[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [shops, setShops] = useState<Option[]>([]);
  const [practitioners, setPractitioners] = useState<Option[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [clients, setClients] = useState<Option[]>([]);

  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [practitionerFilter, setPractitionerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [isBulkHardDeleting, setIsBulkHardDeleting] = useState(false);

  const isBusy = loading || isDeleting || isBulkHardDeleting;
  const { needsShopSelection, canLoad, currentShopId, isAllShopsMode } = useShopScopedPage();

  useEffect(() => {
    void loadOptions();
  }, []);

  useEffect(() => {
    if (currentShopId) {
      setShopFilter(currentShopId);
    }
  }, [currentShopId]);

  useEffect(() => {
    if (!canLoad) return;
    void loadActs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, currentShopId, shopFilter, practitionerFilter, statusFilter, sortKey, sortDir, page]);

  const serviceMap = useMemo(() => {
    const map: Record<string, Option> = {};
    services.forEach((s) => { map[s.id] = s; });
    return map;
  }, [services]);

  const shopMap = useMemo(() => {
    const map: Record<string, Option> = {};
    shops.forEach((s) => { map[s.id] = s; });
    return map;
  }, [shops]);

  const practitionerMap = useMemo(() => {
    const map: Record<string, Option> = {};
    practitioners.forEach((p) => { map[p.id] = p; });
    return map;
  }, [practitioners]);

  const clientMap = useMemo(() => {
    const map: Record<string, Option> = {};
    clients.forEach((c) => { map[c.id] = c; });
    return map;
  }, [clients]);

  // Client-side search on top of the server-paged data
  const filteredActs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return acts.filter((act) => {
      if (serviceFilter && act.serviceId !== serviceFilter) return false;
      if (!term) return true;
      const serviceName = serviceMap[act.serviceId]?.name || '';
      const practitionerName = practitionerMap[act.practitionerId]?.name || '';
      const clientName = act.clientId ? clientMap[act.clientId]?.name || '' : '';
      const notes = act.notes || '';
      return [serviceName, practitionerName, clientName, notes, act.id].join(' ').toLowerCase().includes(term);
    });
  }, [acts, clientMap, practitionerMap, search, serviceFilter, serviceMap]);

  const visibleActIds = useMemo(() => filteredActs.map((a) => a.id), [filteredActs]);
  const {
    selectedIds,
    selectedCount,
    selectedArray,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleActIds);

  const loadOptions = async () => {
    try {
      const [shopsRes, practitionersRes, servicesRes, clientsRes] = await Promise.all([
        apiClient('/api/admin/shops?limit=1000'),
        apiClient('/api/admin/practitioners?limit=1000'),
        apiClient('/api/admin/services?limit=1000'),
        apiClient('/api/admin/clients?limit=1000'),
      ]);
      const [shopsData, practitionersData, servicesData, clientsData] = await Promise.all([
        shopsRes.json(),
        practitionersRes.json(),
        servicesRes.json(),
        clientsRes.json(),
      ]);
      if (shopsData.success) setShops((shopsData.data?.shops || []).map((s: any) => ({ id: s.id, name: s.name })));
      if (practitionersData.success)
        setPractitioners(
          (practitionersData.data?.practitioners || []).map((p: any) => ({
            id: p.id,
            name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email,
          })),
        );
      if (servicesData.success) setServices((servicesData.data?.services || []).map((s: any) => ({ id: s.id, name: s.name })));
      if (clientsData.success)
        setClients(
          (clientsData.data?.clients || []).map((c: any) => ({
            id: c.id,
            name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email,
          })),
        );
    } catch (err) {
      console.error('Failed to load act filters:', err);
      showToast('Impossible de charger les filtres', 'error');
    }
  };

  const loadActs = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getActs({
        shopId: shopFilter || undefined,
        practitionerId: practitionerFilter || undefined,
        status: (statusFilter as ActStatus) || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        page,
        limit: LIMIT,
        sortBy: sortKey,
        sortDir,
      });
      setActs(result.acts);
      setTotal(result.total);
      setTotalPages(result.totalPages);
      clearSelection();
    } catch (err: any) {
      console.error('Load acts error:', err);
      setError(err.message || 'Impossible de charger les actes');
      showToast(err.message || 'Impossible de charger les actes', 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const resetFilters = () => {
    setSearch('');
    setShopFilter('');
    setPractitionerFilter('');
    setServiceFilter('');
    setStatusFilter('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const openDeleteConfirm = (ids: string[], message: string) => {
    setPendingDeleteIds(ids);
    setConfirmTitle('Confirmer la suppression');
    setConfirmMessage(message);
    setIsConfirmOpen(true);
  };

  const handleDelete = (act: Act) => {
    const serviceName = serviceMap[act.serviceId]?.name || 'cet acte';
    openDeleteConfirm([act.id], `Êtes-vous sûr de vouloir supprimer l'acte "${serviceName}" ?`);
  };

  const handleBulkDelete = () => {
    if (selectedArray.length === 0) return;
    openDeleteConfirm(selectedArray, `Supprimer ${selectedArray.length} acte(s) ?`);
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedArray.length === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, ids: selectedArray });
  };

  const handleCreateContract = async (act: Act) => {
    try {
      const res = await apiClient(`/api/admin/acts/${act.id}/contract`, {
        method: 'POST',
        body: JSON.stringify({ type: 'TATTOO_CONSENT', templateVersion: 'v1' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Échec de la création du contrat');
      }
      showToast('Contrat créé avec succès', 'success');
      await loadActs();
    } catch (err: any) {
      showToast(err.message || 'Impossible de créer le contrat', 'error', 7000);
    }
  };

  const handleDeleteContract = async (act: Act) => {
    if (!act.contractId) return;
    if (act.contractSigned) {
      showToast('Impossible de supprimer un contrat signé', 'error');
      return;
    }
    try {
      const res = await apiClient(`/api/admin/contracts/${act.contractId}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Échec de la suppression du contrat');
      }
      showToast('Contrat supprimé', 'success');
      await loadActs();
    } catch (err: any) {
      showToast(err.message || 'Impossible de supprimer le contrat', 'error', 7000);
    }
  };

  const handleGenerateDocument = (act: Act) => {
    if (!act.contractId) {
      showToast('Aucun contrat disponible pour cet acte', 'error');
      return;
    }
    navigate(`/documents?entityType=CONTRACT&entityId=${act.contractId}`);
  };

  const openGeneratedDocument = (documentId: string, format: 'pdf' | 'docx' = 'pdf') => {
    navigate(`/documents/view/${documentId}?format=${format}`);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteIds.length === 0) return;
    setIsDeleting(true);
    const failed: string[] = [];
    try {
      for (const id of pendingDeleteIds) {
        try {
          await deleteAct(id);
        } catch {
          failed.push(id);
        }
      }
      const deletedCount = pendingDeleteIds.length - failed.length;
      if (failed.length === 0) {
        showToast(`${deletedCount} acte(s) supprimé(s)`, 'success');
      } else if (deletedCount === 0) {
        showToast(`Aucun acte supprimé. ${failed.length} échec(s).`, 'error', 7000);
      } else {
        showToast(`${deletedCount} acte(s) supprimé(s). ${failed.length} échec(s).`, 'warning', 7000);
      }
      await loadActs();
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setPendingDeleteIds([]);
      clearSelection();
    }
  };

  const handleConfirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.ids.length === 0) return;
    setIsBulkHardDeleting(true);
    const failed: string[] = [];
    try {
      for (const id of bulkHardDeleteConfirm.ids) {
        try {
          await deleteAct(id, { hardDelete: true });
        } catch {
          failed.push(id);
        }
      }
      const deletedCount = bulkHardDeleteConfirm.ids.length - failed.length;
      if (failed.length === 0) {
        showToast(`${deletedCount} acte(s) supprimé(s) définitivement`, 'success');
      } else if (deletedCount === 0) {
        showToast(`Aucun acte supprimé définitivement. ${failed.length} échec(s).`, 'error', 7000);
      } else {
        showToast(`${deletedCount} acte(s) supprimé(s) définitivement. ${failed.length} échec(s).`, 'warning', 7000);
      }
      setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
      await loadActs();
    } finally {
      setIsBulkHardDeleting(false);
    }
  };

  const SortHeader = ({ label, colKey, icon }: { label: string; colKey: SortKey; icon?: React.ReactNode }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(colKey)}
    >
      <div className="flex items-center space-x-1">
        {icon}
        <span>{label}</span>
        {sortKey === colKey && (
          <ChevronDown className={`w-4 h-4 transform transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
        )}
      </div>
    </th>
  );

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      <ToastComponent />

      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Gestion des Actes</h1>
            <p className="text-textSecondary mt-1">{total} acte{total !== 1 ? 's' : ''} au total</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => void loadActs()}
              className="btn-secondary flex items-center space-x-2"
              disabled={isBusy}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Rafraîchir</span>
            </button>

            <button
              onClick={() => navigate('/planning/acts/create')}
              className="btn-primary flex items-center space-x-2"
              disabled={isBusy}
            >
              <Plus className="w-4 h-4" />
              <span>Nouvel Acte</span>
            </button>

            {selectedCount > 0 && (
              <>
                <button
                  onClick={handleBulkDelete}
                  disabled={isBusy}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Supprimer les actes sélectionnés"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Supprimer ({selectedCount})</span>
                </button>
                {canHardDelete && (
                  <button
                    onClick={openBulkHardDeleteConfirm}
                    disabled={isBusy}
                    className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                    title="Supprimer définitivement les actes sélectionnés"
                  >
                    <AlertCircle className="w-4 h-4" />
                    <span>Suppression définitive ({selectedCount})</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="relative md:col-span-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un acte..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          <div className="relative md:col-span-2">
            <select
              value={shopFilter}
              onChange={(e) => { setShopFilter(e.target.value); setPage(1); }}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les boutiques</option>
              {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative md:col-span-2">
            <select
              value={practitionerFilter}
              onChange={(e) => { setPractitionerFilter(e.target.value); setPage(1); }}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les praticiens</option>
              {practitioners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative md:col-span-2">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative md:col-span-1">
            <button
              onClick={resetFilters}
              className="btn-secondary flex items-center justify-center space-x-2 w-full"
              disabled={isBusy}
            >
              <Filter className="w-4 h-4" />
              <span className="md:hidden">Effacer</span>
            </button>
          </div>
        </div>

        <div className="dt-filters grid grid-cols-1 md:grid-cols-12 gap-4 items-end mt-4">
          <div className="relative md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de début</label>
            <DateInput
              value={fromDate}
              onChange={(value) => { setFromDate(value); setPage(1); }}
              className="input-field"
            />
          </div>
          <div className="relative md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de fin</label>
            <DateInput
              value={toDate}
              onChange={(value) => { setToDate(value); setPage(1); }}
              className="input-field"
            />
          </div>
          <div className="relative md:col-span-2">
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les services</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>
          <div className="md:col-span-6 flex items-end gap-2 flex-wrap">
            <button
              onClick={() => void loadActs()}
              className="btn-primary flex items-center space-x-2"
              disabled={isBusy}
            >
              <span>Filtrer</span>
            </button>
            {selectedCount > 0 && (
              <button
                onClick={clearSelection}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
                disabled={isBusy}
              >
                Vider la sélection
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button onClick={() => void loadActs()} className="mt-2 text-sm text-red-600 hover:text-red-700 underline">
            Réessayer
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={filteredActs.length === 0 || isBusy}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                    aria-label="Sélectionner tous les actes de la page"
                  />
                </th>
                <SortHeader label="Créé le" colKey="createdAt" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Praticien</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isAllShopsMode ? '' : 'hidden'}`}>Boutique</th>
                <SortHeader label="Statut" colKey="status" />
                <SortHeader label="Réalisé le" colKey="performedAt" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contrat</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Chargement...
                    </div>
                  </td>
                </tr>
              ) : filteredActs.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center">
                    <Trash2 className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-900">Aucun acte trouvé</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {search || shopFilter || practitionerFilter || statusFilter || serviceFilter || fromDate || toDate
                        ? 'Essayez de modifier vos filtres.'
                        : 'Commencez par créer votre premier acte.'}
                    </p>
                    {!search && !shopFilter && !practitionerFilter && !statusFilter && !serviceFilter && !fromDate && !toDate && (
                      <div className="mt-4">
                        <button onClick={() => navigate('/planning/acts/create')} className="btn-primary inline-flex items-center space-x-2 mx-auto">
                          <Plus className="w-4 h-4" />
                          <span>Nouvel Acte</span>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filteredActs.map((act) => (
                  <tr key={act.id} className="group hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(act.id)}
                        disabled={isBusy}
                        onChange={() => toggleSelect(act.id)}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                        aria-label={`Sélectionner l'acte ${serviceMap[act.serviceId]?.name || act.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(act.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {serviceMap[act.serviceId]?.name || act.serviceId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {practitionerMap[act.practitionerId]?.name || act.practitionerId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {act.clientId ? clientMap[act.clientId]?.name || act.clientId : '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-600 ${isAllShopsMode ? '' : 'hidden'}`}>
                      {shopMap[act.shopId]?.name || act.shopId}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClasses[act.status]}`}>
                        {statusLabels[act.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(act.performedAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-[180px] truncate" title={act.notes || ''}>
                      {act.notes || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {act.contractStatus ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${contractStatusBadgeClasses[act.contractStatus]}`}>
                          {contractStatusLabels[act.contractStatus]}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {act.documentsCount ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {act.documentsCount}
                          </span>
                          {act.documents?.[0] && (
                            <button
                              onClick={() => openGeneratedDocument(act.documents![0].id, act.documents![0].hasPdf ? 'pdf' : 'docx')}
                              disabled={isBusy}
                              className="p-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
                              title="Voir le dernier document"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex items-center justify-end gap-2">
                        {act.contractId ? (
                          <>
                            <button
                              onClick={() => handleGenerateDocument(act)}
                              disabled={isBusy}
                              className="p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
                              title="Générer le document"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            {!act.contractSigned && (
                              <button
                                onClick={() => void handleDeleteContract(act)}
                                disabled={isBusy}
                                className="p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg disabled:opacity-50"
                                title="Supprimer le contrat"
                              >
                                <FileSignature className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : act.clientId ? (
                          <button
                            onClick={() => void handleCreateContract(act)}
                            disabled={isBusy}
                            className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg disabled:opacity-50"
                            title="Créer un contrat"
                          >
                            <FileSignature className="w-4 h-4" />
                          </button>
                        ) : null}
                        <button
                          onClick={() => navigate(`/planning/acts/${act.id}/edit`)}
                          disabled={isBusy}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-50"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(act)}
                          disabled={isBusy}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{(page - 1) * LIMIT + 1}</span> à{' '}
                <span className="font-medium">{Math.min(page * LIMIT, total)}</span> sur{' '}
                <span className="font-medium">{total}</span> résultats
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isBusy}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      disabled={isBusy}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pageNum
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isBusy}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay for subsequent loads */}
      {loading && acts.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isConfirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        confirmVariant="danger"
        isLoading={isDeleting}
        onConfirm={() => void handleConfirmDelete()}
        onClose={() => {
          if (isDeleting) return;
          setIsConfirmOpen(false);
          setPendingDeleteIds([]);
        }}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={() => void handleConfirmBulkHardDelete()}
        onClose={() => {
          if (isBulkHardDeleting) return;
          setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
        }}
        entityLabel="acte(s)"
        count={bulkHardDeleteConfirm.ids.length}
        isLoading={isBulkHardDeleting}
      />
    </div>
  );
}
